/* ==============================================
   ARTICLE — a single piece ("/insights/:slug").

   Carries .home-page so the palette and type scale
   come from homePage.css, the same way /insights
   and the career centre page do.

   The bar under the navbar is the consistent header
   across every piece: where you are, how far in you
   are, and the way in to RAIN.
   ============================================== */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ChevronRight } from "lucide-react";

import { bySlug, relatedTo, ctaFor } from "../../content/articles";
import { Markdown, headingsOf } from "./markdown";
import { FadeIn, FadeInView } from "../ui/fade-in";
import Seo from "../../seo/Seo";
import { ArticleSchema, BreadcrumbSchema } from "../../seo/StructuredData";
import "../HomePage/homePage.css";
import "./insights.css";
import "./article.css";

const SIGN_UP = "https://app.raincareers.com/auth?mode=signUp";
/* The booking form at the foot of the career centre page. ScrollToTop
   resolves the hash on arrival, so this lands on the form rather than the
   top of that page. */
const BOOK = "/career-centers#book";

/* A standfirst is written for the page, not for a search result, so it can
   run past the ~155 characters Google shows. Trim at the last word that
   fits rather than mid-word. */
const metaDescription = (text, limit = 155) => {
  const flat = text.replace(/\s+/g, " ").trim();
  if (flat.length <= limit) return flat;
  return `${flat.slice(0, limit - 1).replace(/[\s,;:.\u2014-]+\S*$/, "")}\u2026`;
};

/* How far through the body the reader is, as a 0–1 fraction. Measured
   against the body element rather than the document so the footer and the
   related-reading shelf don't count as part of the article. */
const useReadingProgress = (selector) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const el = document.querySelector(selector);
      if (!el) return;
      const { top, height } = el.getBoundingClientRect();
      const run = height - window.innerHeight * 0.4;
      setProgress(run <= 0 ? 1 : Math.min(1, Math.max(0, -top / run)));
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [selector]);

  return progress;
};

/* The contents rail highlights whichever heading the reader is level with. */
const useActiveHeading = (headings) => {
  const [active, setActive] = useState(headings[0]?.id);

  useEffect(() => {
    if (!headings.length) return undefined;

    const onScroll = () => {
      /* The last heading whose top has passed the reading line wins. */
      let current = headings[0].id;
      headings.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 160) current = id;
      });
      setActive(current);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [headings]);

  return active;
};

const TryRain = ({ className = "" }) => (
  <a
    className={`art-try ${className}`.trim()}
    href={SIGN_UP}
    target="_blank"
    rel="noopener noreferrer"
  >
    Try RAIN today
    <ArrowRight size={14} strokeWidth={2.5} />
  </a>
);

const ArticlePage = () => {
  const { slug } = useParams();
  const article = bySlug(slug);

  /* Memoised: the contents rail's scroll effect depends on this array, and a
     fresh one each render would rebind the listener on every scroll tick. */
  const headings = useMemo(() => headingsOf(article?.body || ""), [article]);
  const progress = useReadingProgress(".art-body");
  const active = useActiveHeading(headings);

  if (!article) return <Navigate to="/insights" replace />;

  const { title, kicker, readTime, dek, cover, tone, body, sources, note, date } = article;
  const related = relatedTo(slug);

  return (
    <div className="home-page art-page">
      <Seo
        path={article.url}
        title={`${title} | RAIN`}
        description={metaDescription(dek)}
        image={cover}
        type="article"
      />
      <ArticleSchema
        title={title}
        description={metaDescription(dek)}
        image={cover}
        path={article.url}
        datePublished={date}
      />
      <BreadcrumbSchema
        trail={[
          { name: "Home", path: "/" },
          { name: "Insights", path: "/insights" },
          { name: title, path: article.url },
        ]}
      />

      {/* ---- The consistent header, on every piece ---- */}
      <div className="art-bar">
        <div className="art-bar-inner">
          <Link className="art-back" to="/insights">
            <ArrowLeft size={15} strokeWidth={2.5} />
            <span>Insights</span>
          </Link>

          <span className="art-bar-title">{title}</span>

          <TryRain className="art-try-sm" />
        </div>
        <span
          className="art-progress"
          style={{ transform: `scaleX(${progress})` }}
          aria-hidden="true"
        />
      </div>

      {/* ---- Head ---- */}
      <header className="art-head">
        <div className="home-container">
          <FadeIn delay={0}>
            <p className="art-eyebrow">
              <span>{kicker}</span>
              <span className="art-dot" aria-hidden="true" />
              <span>{readTime}</span>
              {date && (
                <>
                  <span className="art-dot" aria-hidden="true" />
                  <span>{date}</span>
                </>
              )}
            </p>
          </FadeIn>

          <FadeIn delay={0.08}>
            <h1 className="art-h1">{title}</h1>
          </FadeIn>

          <FadeIn delay={0.16}>
            <p className="art-dek">{dek}</p>
          </FadeIn>

          <FadeIn delay={0.24}>
            <div className="art-actions">
              <TryRain />
            </div>
          </FadeIn>
        </div>
      </header>

      <div className="home-container">
        <FadeIn delay={0.3}>
          <figure className={`art-cover res-${tone}`}>
            <img decoding="async" src={cover} alt="" />
          </figure>
        </FadeIn>
      </div>

      {/* ---- Body, with the contents rail beside it ---- */}
      <div className="home-container">
        <div className="art-shell">
          <aside className="art-rail" aria-label="On this page">
            <nav className="art-toc">
              <p className="art-toc-label">On this page</p>
              <ol>
                {headings.map(({ id, text }) => (
                  <li key={id}>
                    <a
                      href={`#${id}`}
                      className={id === active ? "art-toc-on" : undefined}
                      aria-current={id === active ? "true" : undefined}
                    >
                      {text}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          </aside>

          <article className="art-body">
            <Markdown>{body}</Markdown>

            {note && <p className="art-note">{note}</p>}

            <section className="art-sources" aria-labelledby="art-sources-h">
              <h2 id="art-sources-h">Sources</h2>
              <Markdown>{sources}</Markdown>
            </section>
          </article>
        </div>
      </div>

      {/* ---- The way in, at the end ---- */}
      <section className="art-end">
        <div className="home-container">
          <FadeInView>
            {/* The same module the home page closes on — see GetStarted. */}
            <div className="art-end-panel">
              <h2 className="art-end-title">
                Interested in seeing how RAIN works for you?
              </h2>

              <div className="art-end-actions">
                <a
                  className="art-end-primary"
                  href={SIGN_UP}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Try RAIN today
                </a>

                <Link className="art-end-secondary" to={BOOK}>
                  Book a walkthrough
                  <ChevronRight size={16} strokeWidth={2} />
                </Link>
              </div>
            </div>
          </FadeInView>
        </div>
      </section>

      {/* ---- Keep reading ---- */}
      <section className="art-more">
        <div className="home-container">
          <FadeInView>
            <h2 className="home-section-title art-more-title">Keep reading</h2>
          </FadeInView>

          <FadeInView>
            <div className="res-grid art-more-grid">
              {related.map((a) => (
                <Link key={a.slug} className="res-card" to={a.url}>
                  <span className={`res-cover res-${a.tone}`} aria-hidden="true">
                    <img decoding="async" src={a.cover} alt="" loading="lazy" />
                  </span>
                  <span className="res-title">{a.title}</span>
                  <span className="res-read">{a.readTime}</span>
                  <span className="res-cta">
                    {ctaFor(a)}
                    <span className="res-cta-icon" aria-hidden="true">
                      <ArrowRight size={12} strokeWidth={2.5} />
                    </span>
                  </span>
                </Link>
              ))}
            </div>
          </FadeInView>
        </div>
      </section>
    </div>
  );
};

export default ArticlePage;
